import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Compass, Flame, MapPin, TrendingUp, Camera, Sparkles, ExternalLink, Search, Clock } from "lucide-react";
import { TopNav } from "../components/TopNav";
import { api } from "../lib/api";
import { ViralScore, ViralScoreBadge } from "../components/ViralScore";

const TREND_FILTERS = ["All", "Trending", "Emerging", "Hidden Gem", "Oversaturated"];

const TREND_TONES = {
  "Trending": "ring-[#FE0979]/40 text-[#FE0979]",
  "Emerging": "ring-[#00F2FE]/40 text-[#00F2FE]",
  "Hidden Gem": "ring-emerald-400/40 text-emerald-400",
  "Oversaturated": "ring-amber-400/40 text-amber-400",
};

export default function Discover() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [trend, setTrend] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeDest, setActiveDest] = useState(null); // selected destination with spots

  useEffect(() => {
    Promise.all([
      api.get("/discover/destinations"),
      api.get("/discover/stats"),
    ]).then(([a, b]) => {
      setItems(a.data.items || []);
      setStats(b.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let xs = items;
    if (trend !== "All") xs = xs.filter((d) => d.trend_status === trend);
    if (q.trim()) {
      const k = q.toLowerCase();
      xs = xs.filter((d) =>
        d.name.toLowerCase().includes(k) ||
        d.city.toLowerCase().includes(k) ||
        d.country.toLowerCase().includes(k) ||
        (d.aesthetic_tags || []).some((t) => t.toLowerCase().includes(k))
      );
    }
    return xs;
  }, [items, trend, q]);

  const openDestination = async (dest) => {
    setActiveDest({ ...dest, spots: dest.spots || [], _loading: true });
    try {
      const r = await api.get(`/discover/destinations/${dest.id}`);
      setActiveDest(r.data);
    } catch (e) {
      setActiveDest({ ...dest, spots: [], _error: true });
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <TopNav />

      <main className="pt-28 pb-24 px-5 sm:px-8">
        <div className="mx-auto max-w-6xl">
          {/* HEADER */}
          <div className="mb-8">
            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#00F2FE] mb-2 inline-flex items-center gap-2">
              <Compass className="h-3.5 w-3.5" /> Discover
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.98]">
              The viral travel<br /><span className="text-gradient">database</span>.
            </h1>
            <p className="text-zinc-400 mt-3 max-w-xl text-sm sm:text-base">
              {stats ? `${stats.destinations_total} destinations · ${stats.spots_total} viral spots ·` : ""} live trend status, viral score, content format and creator reasons — for every place.
            </p>
          </div>

          {/* STATS STRIP */}
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              {Object.entries(stats.by_trend).map(([k, v]) => (
                <div key={k} data-testid={`stat-${k.replace(/\s+/g,'-').toLowerCase()}`} className="glass rounded-2xl p-4">
                  <div className={`text-[10px] font-bold uppercase tracking-[0.22em] ${TREND_TONES[k]?.split(" ")[1] || "text-zinc-300"}`}>{k}</div>
                  <div className="mt-1 font-display font-black text-3xl">{v}</div>
                </div>
              ))}
            </div>
          )}

          {/* SEARCH + FILTER */}
          <div className="glass-strong rounded-3xl p-3 sm:p-4 mb-8 flex flex-col sm:flex-row gap-3 items-stretch">
            <div className="flex-1 flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4">
              <Search className="h-4 w-4 text-zinc-500 shrink-0" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search city, country, vibe…"
                data-testid="discover-search"
                className="flex-1 bg-transparent outline-none placeholder:text-zinc-500 text-sm py-2.5 font-body"
              />
            </div>
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {TREND_FILTERS.map((f) => (
                <button
                  key={f}
                  onClick={() => setTrend(f)}
                  data-testid={`filter-${f.replace(/\s+/g,'-').toLowerCase()}`}
                  className={`text-[10px] font-bold uppercase tracking-[0.2em] rounded-full px-3 py-2 transition ${trend === f ? "bg-white text-black" : "bg-white/5 text-zinc-300 border border-white/10 hover:bg-white/10"}`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          {/* RESULTS COUNT */}
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-zinc-500 mb-3">
            {loading ? "Loading…" : `${filtered.length} ${filtered.length === 1 ? "destination" : "destinations"}`}
          </div>

          {/* GRID */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((d, i) => (
              <DestinationDbCard key={d.id} dest={d} index={i} onOpen={() => openDestination(d)} />
            ))}
          </div>

          {!loading && filtered.length === 0 && (
            <div className="glass rounded-3xl p-12 text-center text-zinc-400">
              <Sparkles className="h-6 w-6 text-[#00F2FE] mx-auto" />
              <p className="mt-2 text-sm">No matches. Try a different filter or vibe.</p>
            </div>
          )}
        </div>
      </main>

      {/* SPOTS DRAWER */}
      {activeDest && (
        <SpotsDrawer dest={activeDest} onClose={() => setActiveDest(null)} onGenerate={(prompt) => navigate(`/create?q=${encodeURIComponent(prompt)}`)} />
      )}
    </div>
  );
}

const DestinationDbCard = ({ dest, index, onOpen }) => (
  <motion.button
    onClick={onOpen}
    data-testid={`db-dest-${dest.id}`}
    initial={{ opacity: 0, y: 18 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.45, delay: 0.03 * index, ease: [0.2, 0.7, 0.2, 1] }}
    className="text-left group relative aspect-[4/5] sm:aspect-[3/4] rounded-3xl overflow-hidden ring-1 ring-white/10 hover-lift"
  >
    <img src={dest.image} alt={dest.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-[1.05]" />
    <div className="absolute inset-0 scrim-bottom" />
    <div className="absolute top-3 right-3">
      <ViralScoreBadge breakdown={dest.viral_breakdown} fallbackScore={dest.viral_score} testId={`viral-badge-${dest.id}`} />
    </div>
    <div className={`absolute top-3 left-3 glass rounded-full px-2.5 py-1 ring-1 ${TREND_TONES[dest.trend_status]?.split(" ")[0] || "ring-white/15"}`}>
      <span className={`text-[10px] font-bold uppercase tracking-[0.18em] ${TREND_TONES[dest.trend_status]?.split(" ")[1] || ""}`}>
        {dest.trend_status}
      </span>
    </div>
    <div className="absolute inset-x-0 bottom-0 p-4">
      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-300 mb-1.5 inline-flex items-center gap-1.5">
        <MapPin className="h-3 w-3" /> {dest.city}, {dest.country}
      </div>
      <h3 className="font-display font-black text-xl leading-tight text-shadow-soft">{dest.name}</h3>
      <div className="mt-2.5 flex flex-wrap gap-1">
        {(dest.aesthetic_tags || []).slice(0, 3).map((t) => (
          <span key={t} className="text-[9px] font-bold uppercase tracking-[0.16em] bg-white/10 border border-white/15 rounded-full px-2 py-0.5">{t}</span>
        ))}
      </div>
    </div>
  </motion.button>
);

const SpotsDrawer = ({ dest, onClose, onGenerate }) => (
  <div className="fixed inset-0 z-40 flex items-end sm:items-center justify-center px-3" data-testid="spots-drawer">
    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.2, 0.7, 0.2, 1] }}
      className="relative w-full sm:max-w-3xl glass-strong rounded-3xl border border-white/10 overflow-hidden max-h-[88vh] flex flex-col"
    >
      <div className="relative h-44 sm:h-56 shrink-0">
        <img src={dest.image} alt={dest.name} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 scrim-full" />
        <button onClick={onClose} data-testid="drawer-close" className="absolute top-3 right-3 glass rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-white/15 transition">Close</button>
        <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-300 mb-1.5 inline-flex items-center gap-1.5">
            <MapPin className="h-3 w-3" /> {dest.city}, {dest.country}
          </div>
          <h3 className="font-display font-black text-2xl sm:text-3xl text-white text-shadow-soft">{dest.name}</h3>
        </div>
      </div>

      <div className="p-5 sm:p-6 space-y-5 overflow-y-auto">
        <ViralScore
          breakdown={dest.viral_breakdown}
          fallbackScore={dest.viral_score}
          size="md"
          showBreakdown
          testId="drawer-viral-score"
        />

        <div className="flex flex-wrap gap-2">
          <Pill icon={<TrendingUp className="h-3 w-3 text-[#00F2FE]" />}>{dest.trend_status}</Pill>
          <Pill icon={<Clock className="h-3 w-3 text-[#00F2FE]" />}>{dest.best_time_to_shoot}</Pill>
          {dest.maps_link && (
            <a href={dest.maps_link} target="_blank" rel="noreferrer" data-testid="drawer-maps" className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] glass rounded-full px-2.5 py-1 hover:bg-white/10 transition">
              <ExternalLink className="h-3 w-3 text-[#FE0979]" /> Maps
            </a>
          )}
        </div>

        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-400 mb-2">Why it's worth it</div>
          <p className="text-zinc-100 text-sm leading-relaxed font-body">{dest.creator_reason}</p>
        </div>

        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-400 mb-3 flex items-center gap-1.5">
            <Camera className="h-3 w-3 text-[#FE0979]" /> Viral spots ({dest.spots?.length || 0})
          </div>
          <div className="space-y-3">
            {(dest.spots || []).map((s, i) => (
              <SpotRow key={s.id} spot={s} index={i} />
            ))}
          </div>
        </div>

        <button
          onClick={() => onGenerate(`${dest.aesthetic_tags?.[0] || ""} trip in ${dest.city}, ${dest.country} — ${dest.creator_reason}`)}
          data-testid="drawer-generate"
          className="w-full inline-flex items-center justify-center gap-2 gradient-pink-cyan text-black font-display font-bold uppercase tracking-[0.18em] text-xs rounded-full py-3.5 hover:opacity-95 active:scale-[0.99] transition"
        >
          <Sparkles className="h-4 w-4" /> Build my AI trip from this
        </button>
      </div>
    </motion.div>
  </div>
);

const Pill = ({ icon, children }) => (
  <span className="glass rounded-full px-2.5 py-1 inline-flex items-center gap-1.5">
    {icon} <span className="text-[10px] font-bold uppercase tracking-[0.18em]">{children}</span>
  </span>
);

const SpotRow = ({ spot, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35, delay: 0.04 * index }}
    data-testid={`db-spot-${spot.id}`}
    className="glass rounded-2xl p-4"
  >
    <div className="flex items-center justify-between mb-2 gap-3">
      <div className="font-display font-bold text-base leading-tight">{spot.name}</div>
      <span className="glass rounded-full px-2 py-0.5 inline-flex items-center gap-1 shrink-0">
        <Flame className="h-3 w-3 text-[#FE0979]" />
        <span className="font-display font-bold text-[10px]">{spot.viral_score}</span>
      </span>
    </div>
    <div className="flex flex-wrap gap-1.5 mb-3">
      <span className="text-[9px] font-bold uppercase tracking-[0.18em] bg-[#FE0979]/15 text-[#FE0979] rounded-full px-2 py-0.5 ring-1 ring-[#FE0979]/30">
        {spot.content_format}
      </span>
      <span className="text-[9px] font-bold uppercase tracking-[0.18em] bg-white/5 border border-white/10 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
        <Clock className="h-2.5 w-2.5" /> {spot.best_time}
      </span>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs leading-relaxed">
      <Field label="Best photo angle">{spot.best_photo_angle}</Field>
      <Field label="Reel idea">{spot.reel_idea}</Field>
      <Field label="Caption idea"><span className="italic">"{spot.caption_idea}"</span></Field>
      <Field label="Nearby">{spot.nearby}</Field>
    </div>
  </motion.div>
);

const Field = ({ label, children }) => (
  <div>
    <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-zinc-500 mb-0.5">{label}</div>
    <div className="text-zinc-200 font-body">{children}</div>
  </div>
);
